<template>
    <div class="main mx-auto">
        <div class="w-full max-w-xs mx-auto">
            <div class="text-center">
                <div v-if="monthlyInvitesRemaining === 1">
                    <h2 class="pb-6">{{ monthlyInvitesRemaining }} {{ $t('FREE_FLOWERS_FREE_BOX') }}</h2>
                </div>
                <div v-else-if="monthlyInvitesRemaining === 0">
                    <h2 class="pb-6">{{ $t('FREE_FLOWERS_NO_BOXES') }}</h2>
                </div>
                <div v-else>
                    <h2 class="pb-6">{{ monthlyInvitesRemaining }} {{ $t('FREE_FLOWERS_FREE_BOXES') }}</h2>
                </div>
                <div v-if="monthlyInvitesRemaining">
                    <img class="mx-auto pb-6 w-24"
                         :src="require(`@/assets/images/pages/free-flowers/boxes-${monthlyInvitesRemaining}.gif`)">

                    <p class="font-bold pb-6">{{ $t('FREE_FLOWERS_SEND_INVITE') }}</p>
                </div>
            </div>

            <div v-if="monthlyInvitesRemaining">
                <first-name-field v-model="name" :label="$t('FREE_FLOWERS_FRIENDS_FIRST')"/>

                <legacy-email-address-field
                    v-model="email"
                    :validation.sync="formValidations.email"
                    :label="$t('FREE_FLOWERS_EMAIL')"/>

                <gift-note-field v-model="giftnote" :label="$t('FREE_FLOWERS_GIFT_NOTE')"/>

                <p class="text-center" v-html="message"></p>
                <p class="text-center error" v-html="error"></p>

                <div class="w-full my-1 text-center mb-8">
                    <submit-button
                        @click="submitForm()" :processing="isFormProcessing">
                        {{ $t('FREE_FLOWERS_SEND') }}
                    </submit-button>
                </div>
            </div>
        </div>
        <paper-section>
            <div class="text-center py-10">
                <h2 v-text="$t('FREE_FLOWERS_HOW_DOES')"></h2>
                <p v-text="$t('FREE_FLOWERS_EACH_MONTH')"></p>
                <p v-text="$t('FREE_FLOWERS_JUST_SEND')"></p>
                <p v-text="$t('FREE_FLOWERS_WE_ALSO')"></p>
                <h2 class="pt-6" v-text="$t('FREE_FLOWERS_YOUR_FRIEND')"></h2>
                <p class="text-center" v-text="$t('FREE_FLOWERS_IF_YOU')"></p>
                <h3 class="text-center" v-html="friendCode"></h3>
            </div>
        </paper-section>
        <h2 v-if="hasExistingReferrals" class="text-center p-4" v-text="$t('FREE_FLOWERS_YOUR_INVITES')"></h2>
        <paper-section v-if="hasExistingReferrals">
            <div class="container mx-auto p-8">
                <div class="flex flex-wrap">
                    <div v-for="(referral, index) in referrals" :key="index" class="lg:w-1/3 md:w-1/2 w-full">
                        <card-invite
                            :key="index"
                            @invite-updated="getReferrals"
                            :referral="referral"/>
                    </div>
                </div>
            </div>
        </paper-section>
        <div class="text-center p-4 pb-8">
            <h2 v-text="$t('FREE_FLOWERS_CONFUSED')"></h2>
            <p>
                {{ $t('FREE_FLOWERS_VIEW') }}
                <router-link class="link" to="/faq/free-flower">
                    {{ $t('FREE_FLOWERS_FAQ') }}
                </router-link>
            </p>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import required from 'vuelidate/src/validators/required';
import { gaEngagement } from '@/helpers/GAHelpers';
import ReferralService from '@/services/users/ReferralService';
import PaperSection from '@/components/structure/PaperSection';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import FirstNameField from '@/components/forms/fields/FirstNameField';
import LegacyEmailAddressField from '@/components/forms/fields/LegacyEmailAddressField';
import CardInvite from '@/components/partials/account/free-flowers/CardInvite';
import GiftNoteField from '@/components/forms/fields/GiftNoteField';

let referralService = new ReferralService();

export default {
    name: 'FreeFlowers',
    data() {
        return {
            formValidations: {
                email: false,
            },
            isFormProcessing: false,
            referrals: [],
            message: '',
            error: '',
            email: null,
            name: null,
            giftnote: null,
        };
    },
    computed: {
        ...mapGetters({
            numberOfBoxes: 'account/getUserReferralsRemaining',
            numberOfInvitesRemaining: 'account/getUserInvitesRemaining',
            friendCode: 'account/getUserFriendCode',
        }),

        areAllFieldsValid() {
            return !this.$v.$invalid;
        },

        hasExistingReferrals() {
            return this.referrals.length > 0;
        },

        boxesRemaining() {
            return this.numberOfBoxes > 0;
        },

        monthlyInvitesRemaining() {
            return this.numberOfInvitesRemaining;
        },
    },
    mounted() {
        this.getReferrals();
    },
    methods: {
        resetMessages() {
            if (this.name === null) this.name = '';
            if (this.email === null) this.email = '';
            if (this.giftnote === null) this.giftnote = '';
        },

        getReferrals() {
            referralService.getReferrals()
                .then(response => {
                    this.referrals = response.data.items;
                })
                .catch(() => {
                    this.error = this.$t('FREE_FLOWERS_SOMETHING_WENT_WRONG');
                });
        },

        postReferral() {
            referralService.postReferral(this.name, this.email, this.giftnote)
                .then(() => {
                    this.message = this.$t('FREE_FLOWERS_THANKS', {
                        'name': this.$t(`${this.name}`),
                    });

                    this.name = null;
                    this.email = null;
                    this.giftnote = null;

                    this.getReferrals();
                    this.$store.dispatch('account/fetchUserData', true);
                })
                .catch(() => {
                    this.error = this.$t('FREE_FLOWERS_SOMETHING_WENT_WRONG');
                });
        },

        submitForm() {
            this.toggleFormProcessing();

            setTimeout(() => {
                this.validateForm();
                gaEngagement('click', 'Send invite');
            }, 1000);
        },

        toggleFormProcessing() {
            this.isFormProcessing = !this.isFormProcessing;
        },

        validateForm() {
            this.$v.$touch();

            this.areAllFieldsValid ? this.postReferral() : this.resetMessages();

            this.toggleFormProcessing();
        },
    },
    validations: {
        email: {
            isValid() {
                return this.formValidations.email;
            },
        },
        name: {
            required,
        },
    },
    components: {
        LegacyEmailAddressField,
        FirstNameField,
        PaperSection,
        CardInvite,
        SubmitButton,
        GiftNoteField,
    },
};
</script>
